import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { RootState } from "@/store";
import { BasePermissions } from "@/constants/permission-modules/base-permissions";
import { Route } from "@/router";
import { usePermissionState } from "@/providers/PermissionProvider";
import { toast } from "react-toastify";
interface AuthRequiredProps {
  children: React.ReactNode;
  route: Route;
}

const AuthRequired = ({ children, route }: AuthRequiredProps) => {
  const { isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { isPermitted } = usePermissionState();

  const getCurrentRoutePermissions: () => Partial<BasePermissions>[] =
    useCallback(() => {
      let currentRoute: Route | undefined = route;
      const currentPath = location.pathname;

      if (currentPath !== route.path && route.children) {
        currentRoute = route.children?.find((child) => {
          return route.path + "/" + child.path === currentPath;
        });
      }

      return currentRoute?.permissions || [];
    }, [route, location]);

  const isPermissionGrantedToModule = useCallback(() => {
    const currentRoutePermissions = getCurrentRoutePermissions();
    if (
      currentRoutePermissions.length === 0 ||
      user?.permissions?.length === 0
    ) {
      return true;
    }

    return currentRoutePermissions.some((permission) => {
      if (isPermitted(permission)) {
        return true;
      }
    });
  }, [getCurrentRoutePermissions, isPermitted, user]);

  useEffect(() => {
    const isModuleAccessAllowed = isPermissionGrantedToModule();
    if (!isAuthenticated) {
      // Redirect to login while saving the attempted URL
      navigate("/", {
        replace: true,
        state: { from: location.pathname },
      });
    } else if (!isModuleAccessAllowed) {
      toast.error("You are not authorized to access this module");
      navigate("/", {
        replace: true,
      });
    }
  }, [isAuthenticated, navigate, location, route, isPermissionGrantedToModule]);

  return isAuthenticated ? <>{children}</> : null;
};

export default AuthRequired;
